import { useState, useEffect, useRef } from 'react';
import { Modal } from '~/components/ui/Modal';
import axios from 'axios';
import { createKeys, type ApiKeys } from '~/utils/OauthHelper';
import { toast } from 'react-toastify';
import { X, LogOut, Key, Image as ImageIcon } from 'lucide-react';
import { classNames } from '~/utils/classNames';
import { setUserData, userDataStore } from '~/lib/stores/integrations';
import { useStore } from '@nanostores/react';
import { useLoaderData } from '@remix-run/react';
import * as Sentry from '@sentry/remix';
import { userStore } from '~/lib/stores/user';

interface OAuthModalProps {
  isOpen: boolean;
  onClose: () => void;
  onRemoveAttachedImage: () => void;
  onExtractComplete: (data: { image: string | null; fonts: string[]; colors: string[]; imageUrls: string[] }) => void;
  onExtractError?: (error: { image: string | null; errorType: 'size' | 'dimension' }) => void;
}

type AuthFrontendEnvs = {
  FIGMA_CLIENT_ID: string;
  FIGMA_REDIRECT_URI: string;
};

interface StyleInfo {
  fonts: Set<string>;
  colors: Set<string>;
  images: string[];
}

interface Color {
  r: number;
  g: number;
  b: number;
  a?: number;
}

interface ApiResponse {
  message: string;
  data: ApiKeys;
}

// Define types for better code organization
interface FigmaImageResult {
  imageBase64: string;
  imageSizeInMB: number;
  fileId: string;
  nodeId: string;
}

interface ImageProcessingOptions {
  scale: number;
  format: string;
  useAbsoluteBounds: boolean;
}

// Define types for better code organization
interface FigmaImageResult {
  imageBase64: string;
  imageSizeInMB: number;
  fileId: string;
  nodeId: string;
}

interface ImageProcessingOptions {
  scale: number;
  format: string;
  useAbsoluteBounds: boolean;
}

// Add this interface near the top with other interfaces
interface GradientStop {
  color: string;
  position: number;
  opacity: number;
}

// Add new function to validate Figma token
const validateFigmaToken = async (token: string): Promise<boolean> => {
  try {
    const isValid = await Sentry.startSpan(
      {
        name: 'figmaTokenValidation',
        op: 'figamTokenValidation',
      },
      async () => {
        const response = await axios.get('https://api.figma.com/v1/me', {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        });

        /*
         * console.log('Figma token validation response:', {
         *   status: response.status,
         *   data: response.data,
         * });
         */

        setUserData(response.data);
        return response.status === 200;
      },
    );

    return isValid;
  } catch (error) {
    if (axios.isAxiosError(error) && error.response) {
      const status = error.response.status;

      switch (status) {
        case 401: {
          toast.error('Unauthorized: Invalid Figma token. Please reconnect your account.');
          break;
        }
        case 403: {
          toast.error('Forbidden: Access denied. Please check your permissions.');
          break;
        }
        case 404: {
          toast.error('Not Found: The requested resource could not be found.');
          break;
        }
        case 500: {
          toast.error('Server Error: Please try again later.');
          break;
        }
        default: {
          toast.error('An error occurred. Please try again.');
          break;
        }
      }
    } else {
      toast.error('Network error. Please check your connection.');
    }

    console.error('Token validation error:', error);

    Sentry.captureException(error, {
      extra: {
        message: 'Error validating Figma token',
        token,
      },
    });

    return false;
  }
};

const MAX_DIMENSION = 8000; // Add this constant at the top with other constants

const getImageDimensions = (base64: string): Promise<{ width: number; height: number }> => {
  return new Promise((resolve, reject) => {
    const img = new Image();

    img.onload = () => {
      resolve({
        width: img.width,
        height: img.height,
      });
    };
    img.onerror = reject;
    img.src = base64;
  });
};

export function OauthModal({
  isOpen,
  onClose,
  onExtractComplete,
  onRemoveAttachedImage,
  onExtractError,
}: OAuthModalProps): JSX.Element {
  // console.log('>>>[OAuthModal] isOpen: ', isOpen); // this component rerenders multiple times since we get this log baar baar

  const env = useLoaderData<AuthFrontendEnvs>();
  const { email } = userStore.get();
  const [keys, setKeys] = useState<ApiKeys | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [figmaUrl, setFigmaUrl] = useState('');
  const [hasFigmaToken, setHasFigmaToken] = useState(false);
  const [initValidationLoading, setInitValidationLoading] = useState(true);
  const pollCount = useRef(0);
  const pollInterval = useRef<NodeJS.Timeout>();
  const [imageDetails, setImageDetails] = useState<{
    quality: number;
    size: number;
  }>({
    quality: 0,
    size: 0,
  });
  const imageSizeLimit = 1;

  // const toast = useToast();
  const [imagePreview, setImagePreview] = useState<string | null>(null);
  const [isExtracting, setIsExtracting] = useState(false);
  const [isExtractionDone, setIsExtractionDone] = useState(false);
  const [styleInfo, setStyleInfo] = useState<StyleInfo>({
    fonts: new Set(),
    colors: new Set(),
    images: [],
  });

  const userData = useStore(userDataStore);
  console.log('userData in oauth modal', userData);

  // Add dimensions state near the top with other state declarations
  const [dimensions, setDimensions] = useState<{ width: number; height: number } | null>(null);

  // Add an abort controller reference
  const abortControllerRef = useRef<AbortController | null>(null);

  const validateInitialToken = async () => {
    const token = localStorage.getItem('dualite_figma_token');

    if (token) {
      const isValid = await validateFigmaToken(token);

      if (!isValid) {
        localStorage.removeItem('dualite_figma_token');
        localStorage.removeItem('dualite_api_keys');
        setHasFigmaToken(false);
        setKeys(null);
        toast.error('Figma token expired. Please reconnect your account.');
      } else {
        setHasFigmaToken(true);
        setIsLoading(false);
      }
    }

    setInitValidationLoading(false);
  };

  const checkForFigmaToken = async () => {
    const token = localStorage.getItem('dualite_figma_token');

    // console.log('Checking for token:', token, 'Attempt:', pollCount.current + 1);

    if (token) {
      // Validate the token before proceeding
      const isValid = await validateFigmaToken(token);

      if (!isValid) {
        // Clear invalid token and restart OAuth flow
        localStorage.removeItem('dualite_figma_token');
        localStorage.removeItem('dualite_api_keys');
        setHasFigmaToken(false);
        setKeys(null);
        toast.error('Figma token expired. Please reconnect your account.');

        if (pollInterval.current) {
          clearInterval(pollInterval.current);
        }

        return false;
      }

      setHasFigmaToken(true);

      if (pollInterval.current) {
        clearInterval(pollInterval.current);
      }

      // Update state
      setHasFigmaToken(true);
      setIsLoading(false);
      toast.success('Successfully connected to Figma');
      return true;
    }

    pollCount.current += 1;

    if (pollCount.current >= 15) {
      // 5 attempts * 3 seconds = 15 seconds
      if (pollInterval.current) {
        clearInterval(pollInterval.current);
      }

      toast.error('Failed to connect to Figma. Please try again.');
      setIsLoading(false);
      onClose();
      return false;
    }

    return false;
  };

  useEffect(() => {
    setInitValidationLoading(true);
    validateInitialToken();

    return () => {
      if (pollInterval.current) {
        clearInterval(pollInterval.current);
      }
    };
  }, []);

  const handleGenerateKeys = async () => {
    setIsLoading(true);

    try {
      const response = (await createKeys()) as ApiResponse;

      // console.log('response from modal', response.data); // remove logs of critical data after testing (everyone) @sayan404 @shivamagarwal2510 @vaibhavityagi

      if (response.data) {
        localStorage.setItem('dualite_api_keys', JSON.stringify(response.data));

        pollCount.current = 0;

        if (pollInterval.current) {
          clearInterval(pollInterval.current);
        }

        // hardcoded tokens?? are you kidding me @sayan404
        const figmaOAuthUrl = `https://www.figma.com/oauth?client_id=${env.FIGMA_CLIENT_ID}&redirect_uri=${env.FIGMA_REDIRECT_URI}&scope=files:read&state=${response.data.writeKey}&response_type=code`;
        window.open(figmaOAuthUrl, '_blank');
        pollInterval.current = setInterval(checkForFigmaToken, 3000);
      } else {
        throw new Error('No keys received from server');
      }
    } catch (error) {
      Sentry.captureException(error, {
        extra: {
          message: 'Error generating API keys',
        },
      });
      toast.error(error instanceof Error ? error.message : 'Failed to generate API keys');
      setIsLoading(false);
    }
  };

  const rgbaToHex = (color: Color): string => {
    try {
      if (!color || typeof color.r !== 'number' || typeof color.g !== 'number' || typeof color.b !== 'number') {
        return 'transparent';
      }

      const r = Math.max(0, Math.min(255, Math.round(color.r * 255)));
      const g = Math.max(0, Math.min(255, Math.round(color.g * 255)));
      const b = Math.max(0, Math.min(255, Math.round(color.b * 255)));

      return `#${((1 << 24) + (r << 16) + (g << 8) + b).toString(16).slice(1).toUpperCase()}`;
    } catch (error) {
      Sentry.captureException(error, {
        extra: {
          message: 'Error converting color to hex',
          color,
        },
      });
      console.warn('Error converting color to hex:', error);
      return 'transparent';
    }
  };

  // Extract common Figma image fetching logic into a reusable function - using Strategy pattern
  const fetchFigmaImage = async (
    urlString: string,
    options: Partial<ImageProcessingOptions> = {},
  ): Promise<FigmaImageResult> => {
    // Default options with strategy pattern - changed default scale to 1
    const processingOptions: ImageProcessingOptions = {
      scale: options.scale || 1, // Changed default scale to 1
      format: options.format || 'png',
      useAbsoluteBounds: options.useAbsoluteBounds !== undefined ? options.useAbsoluteBounds : true,
    };

    // Extract Figma file ID and node ID from the URL
    const urlPattern = /design\/([^/]+).*node-id=([^&]+)/;
    const matches = urlString.match(urlPattern);

    if (!matches) {
      throw new Error('Invalid Figma URL format. Please use the share URL from Figma design.');
    }

    const [, fileId, rawNodeId] = matches;
    const nodeId = rawNodeId.replace('-', ':');
    const figmaToken = localStorage.getItem('dualite_figma_token');

    if (!figmaToken) {
      throw new Error('Figma token not found. Please reconnect your Figma account.');
    }

    // Use the current abort controller's signal
    const signal = abortControllerRef.current?.signal;

    // Call Figma API with specified scale and signal
    const response = await axios.get(`https://api.figma.com/v1/images/${fileId}`, {
      params: {
        ids: nodeId,
        scale: processingOptions.scale,
        format: processingOptions.format,
        use_absolute_bounds: processingOptions.useAbsoluteBounds,
      },
      headers: {
        Authorization: `Bearer ${figmaToken}`,
      },
      signal, // Add the abort signal
    });

    // console.log('Figma API response:', response.data);

    if (response.data.err) {
      throw new Error(`Figma API Error: ${response.data.err}`);
    }

    const imageUrl = response.data.images[nodeId];

    if (!imageUrl) {
      throw new Error('No image URL found in the response');
    }

    // Fetch the actual image with signal
    console.log('Fetching image from:', imageUrl);

    const imageResponse = await axios.get(imageUrl, {
      responseType: 'arraybuffer',
      signal, // Add the abort signal
    });

    // Calculate image size
    const imageSizeInMB = imageResponse.data.byteLength / (1024 * 1024);
    console.log('Image size:', imageSizeInMB.toFixed(2), 'MB');

    // Convert to base64
    const base64Image = Buffer.from(imageResponse.data, 'binary').toString('base64');
    const imageBase64 = `data:image/png;base64,${base64Image}`;

    return {
      imageBase64,
      imageSizeInMB,
      fileId,
      nodeId,
    };
  };

  // Add constants for size thresholds
  const THRESHOLD_SIZE = 2; // 2MB
  const MAX_SIZE = 4; // 4MB

  const handleCancel = () => {
    // Cancel any ongoing API requests
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
      abortControllerRef.current = null;
    }

    if (pollInterval.current) {
      clearInterval(pollInterval.current);
    }

    // Reset extraction states but don't close modal if we're just extracting
    if (isExtracting) {
      setIsExtracting(false);
      setImagePreview(null);
      setFigmaUrl('');
      setStyleInfo({
        fonts: new Set(),
        colors: new Set(),
        images: [],
      });
      setDimensions(null);
      setImageDetails({
        quality: 0,
        size: 0,
      });

      // Clear any extraction data
      onExtractComplete({
        image: null,
        fonts: [],
        colors: [],
        imageUrls: [],
      });

      return; // Don't close the modal, just return to input screen
    }

    // If not in extraction mode, close the modal as before
    onClose();
  };

  const handleImageUrlSubmit = async () => {
    // Create a new AbortController for this request
    if (!isValidFigmaUrl(figmaUrl)) {
      toast.error('Invalid Figma URL. Please enter a valid Figma design URL.');
      return;
    }

    abortControllerRef.current = new AbortController();

    const signal = abortControllerRef.current.signal;

    setIsExtracting(true);

    const figmaToken = localStorage.getItem('dualite_figma_token');

    // console.log('token in handleImageUrlSubmit: ', figmaToken);

    if (figmaToken) {
      const isValid = await validateFigmaToken(figmaToken);
      console.log('isValid in handleImageUrlSubmit: ', isValid);

      if (!isValid) {
        console.log('if hitting');
        localStorage.removeItem('dualite_figma_token');
        localStorage.removeItem('dualite_api_keys');
        setHasFigmaToken(false);
        setImagePreview(null);
        setFigmaUrl('');
        setKeys(null);
        toast.error('Figma token expired. Please reconnect your account.');
        return;
      }
    }

    if (!figmaToken) {
      setHasFigmaToken(false);
      setImagePreview(null);
      setFigmaUrl('');
      setKeys(null);
      toast.error('Please reconnect your Figma account.');
      return;
    }

    if (!figmaUrl) {
      toast.error('Please enter a Figma image URL');
      return;
    }

    if (isExtractionDone) {
      onClose();
      return;
    }

    try {
      // Step 1: Fetch node data for style extraction
      const urlPattern = /design\/([^/]+).*node-id=([^&]+)/;
      const matches = figmaUrl.match(urlPattern);

      if (!matches) {
        throw new Error('Invalid Figma URL format. Please use the share URL from Figma design.');
      }

      const [, fileId, rawNodeId] = matches;
      const nodeId = rawNodeId.replace('-', ':');

      console.log('nodeId in handleImageUrlSubmit: ', matches, '>>>>>>:', nodeId, fileId, rawNodeId);

      await Sentry.startSpan(
        {
          name: 'getFigmaData',
          op: 'getFigmaData',
        },
        async () => {
          // First, fetch the specific node data
          const fileResponse = await axios.get(`https://api.figma.com/v1/files/${fileId}/nodes?ids=${nodeId}`, {
            headers: {
              Authorization: `Bearer ${figmaToken}`,
            },
            signal, // Add abort signal to axios requests
          });

          console.log('Node data:', fileResponse.data);

          const nodeData = fileResponse.data.nodes[nodeId];

          if (!nodeData) {
            throw new Error('Failed to extract design. Please make sure you have selected a valid design element.');
          }

          const fonts = new Set<string>();
          const colors = new Set<string>();
          const imageRefs = new Set<string>();

          const processNode = (node: any) => {
            try {
              if (node?.style) {
                if (node.style.fontFamily) {
                  fonts.add(node.style.fontFamily);
                }

                if (node.style.fontPostScriptName) {
                  fonts.add(node.style.fontPostScriptName);
                }

                // Handle font weights
                if (node.style.fontWeight) {
                  fonts.add(`${node.style.fontFamily}-${node.style.fontWeight}`);
                }
              }

              // Process fills for colors with mixed types
              if (Array.isArray(node?.fills)) {
                node.fills.forEach((fill: any) => {
                  try {
                    if (fill?.type === 'SOLID' && fill?.color) {
                      // Handle solid colors with opacity
                      const hexColor = rgbaToHex(fill.color);
                      const opacity = fill.opacity !== undefined ? fill.opacity : 1;
                      const colorValue =
                        opacity < 1
                          ? `${hexColor}${Math.round(opacity * 255)
                              .toString(16)
                              .padStart(2, '0')}`
                          : hexColor;

                      colors.add(
                        JSON.stringify({
                          type: 'SOLID',
                          color: colorValue,
                          opacity,
                        }),
                      );
                    } else if (
                      (fill?.type === 'GRADIENT_LINEAR' || fill?.type === 'GRADIENT_RADIAL') &&
                      Array.isArray(fill?.gradientStops)
                    ) {
                      // Handle gradient colors with better validation
                      const validGradientStops = fill.gradientStops
                        .filter((stop: any) => stop?.color && typeof stop.position === 'number')
                        .map((stop: any) => ({
                          color: rgbaToHex(stop.color),
                          position: stop.position,
                          opacity: stop.opacity !== undefined ? stop.opacity : 1,
                        }));

                      if (validGradientStops.length > 0) {
                        // Add each gradient stop color to the colors set
                        validGradientStops.forEach((stop: GradientStop) => {
                          colors.add(
                            JSON.stringify({
                              type: 'SOLID',
                              color: stop.color,
                              opacity: stop.opacity,
                            }),
                          );
                        });

                        // Also add the full gradient information
                        colors.add(
                          JSON.stringify({
                            type: fill.type,
                            gradientStops: validGradientStops,
                            gradientTransform: fill.gradientTransform ?? [],
                            opacity: fill.opacity !== undefined ? fill.opacity : 1,
                          }),
                        );
                      }
                    } else if (fill?.type === 'GRADIENT_ANGULAR' || fill?.type === 'GRADIENT_DIAMOND') {
                      // Handle other gradient types
                      const validGradientStops = fill.gradientStops
                        .filter((stop: any) => stop?.color && typeof stop.position === 'number')
                        .map((stop: any) => ({
                          color: rgbaToHex(stop.color),
                          position: stop.position,
                          opacity: stop.opacity !== undefined ? stop.opacity : 1,
                        }));

                      if (validGradientStops.length > 0) {
                        // Add each gradient stop color
                        validGradientStops.forEach((stop: GradientStop) => {
                          colors.add(
                            JSON.stringify({
                              type: 'SOLID',
                              color: stop.color,
                              opacity: stop.opacity,
                            }),
                          );
                        });

                        // Add the full gradient information
                        colors.add(
                          JSON.stringify({
                            type: fill.type,
                            gradientStops: validGradientStops,
                            gradientTransform: fill.gradientTransform ?? [],
                            opacity: fill.opacity !== undefined ? fill.opacity : 1,
                          }),
                        );
                      }
                    } else if (fill?.type === 'IMAGE' && fill?.imageRef) {
                      // Handle image fills
                      imageRefs.add(fill.imageRef);
                    }
                  } catch (fillError) {
                    console.warn('Error processing fill:', fillError);
                  }
                });
              }

              // Process mixed background styles
              if (node?.styles) {
                Object.entries(node.styles).forEach(([styleType, styleId]: [string, any]) => {
                  try {
                    if (styleType === 'fill' || styleType === 'text') {
                      // Handle style references
                      if (node?.fillStyleId) {
                        colors.add(
                          JSON.stringify({
                            type: 'STYLE_REF',
                            styleId: node.fillStyleId,
                            name: styleType,
                          }),
                        );
                      }
                    }
                  } catch (styleError) {
                    console.warn('Error processing style:', styleError);
                  }
                });
              }

              // Process strokes with mixed types
              if (Array.isArray(node?.strokes)) {
                node.strokes.forEach((stroke: any) => {
                  try {
                    if (stroke?.type === 'SOLID' && stroke?.color) {
                      const hexColor = rgbaToHex(stroke.color);
                      const opacity = stroke.opacity !== undefined ? stroke.opacity : 1;

                      colors.add(
                        JSON.stringify({
                          type: 'STROKE',
                          color: hexColor,
                          opacity,
                          weight: node.strokeWeight,
                        }),
                      );
                    }
                  } catch (strokeError) {
                    console.warn('Error processing stroke:', strokeError);
                  }
                });
              }

              // Process mixed background colors
              if (node?.backgroundColor) {
                try {
                  const hexColor = rgbaToHex(node.backgroundColor);
                  const opacity = node.backgroundOpacity !== undefined ? node.backgroundOpacity : 1;

                  colors.add(
                    JSON.stringify({
                      type: 'BACKGROUND',
                      color: hexColor,
                      opacity,
                    }),
                  );
                } catch (bgError) {
                  console.warn('Error processing background color:', bgError);
                }
              }

              // Process children recursively
              if (Array.isArray(node?.children)) {
                node.children.forEach((child: any) => {
                  try {
                    processNode(child);
                  } catch (childError) {
                    console.warn('Error processing child node:', childError);
                  }
                });
              }
            } catch (nodeError) {
              console.warn('Error processing node:', nodeError);
            }
          };

          processNode(nodeData.document);

          // Safely convert sets to arrays with error handling
          let colorArray: any[] = [];

          try {
            colorArray = Array.from(colors)
              .map((colorStr) => {
                try {
                  const colorData = JSON.parse(colorStr);

                  // Include both solid colors and gradients in the final array
                  if (colorData.type === 'SOLID') {
                    return colorData;
                  } else if (
                    ['GRADIENT_LINEAR', 'GRADIENT_RADIAL', 'GRADIENT_ANGULAR', 'GRADIENT_DIAMOND'].includes(
                      colorData.type,
                    )
                  ) {
                    return {
                      type: colorData.type,
                      colors: colorData.gradientStops.map((stop: any) => ({
                        color: stop.color,
                        opacity: stop.opacity,
                        position: stop.position,
                      })),
                      transform: colorData.gradientTransform,
                    };
                  }

                  return null;
                } catch {
                  return null;
                }
              })
              .filter(Boolean); // Remove any null values
          } catch (error) {
            console.warn('Error processing colors:', error);
            colorArray = []; // Use empty array if processing fails
          }

          let fontsArray: string[] = [];

          try {
            fontsArray = Array.from(fonts);
          } catch (error) {
            console.warn('Error processing fonts:', error);
            fontsArray = []; // Use empty array if processing fails
          }

          // Update state and localStorage only with valid data
          setStyleInfo({
            fonts: new Set(fontsArray),
            colors: new Set(
              colorArray
                .map((color) => {
                  if (color.type === 'SOLID') {
                    return color.color;
                  } else if (color.colors) {
                    // For gradients, include all stop colors
                    return color.colors.map((stop: any) => stop.color);
                  }

                  return null;
                })
                .flat()
                .filter(Boolean),
            ),
            images: [], // Will be updated later with image URLs
          });

          // Safely store data in localStorage
          try {
            localStorage.setItem('dualite_figma_colors', JSON.stringify(colorArray));
            localStorage.setItem('dualite_figma_fonts', JSON.stringify(fontsArray));
          } catch (storageError) {
            console.warn('Error storing data in localStorage:', storageError);

            // Continue execution even if localStorage fails
          }

          let imageUrls: string[] = [];

          if (imageRefs.size > 0) {
            const imagesResponse = await axios.get(`https://api.figma.com/v1/files/${fileId}/images`, {
              headers: {
                Authorization: `Bearer ${figmaToken}`,
              },
            });
            console.log('Images response:', imagesResponse.data);

            imageUrls = Array.from(imageRefs)
              .map((ref) => imagesResponse.data.meta.images[ref])
              .filter(Boolean);
          }

          setStyleInfo({
            fonts: new Set(fontsArray),
            colors: new Set(
              colorArray
                .map((color) => {
                  if (color.type === 'SOLID') {
                    return color.color;
                  } else if (color.colors) {
                    // For gradients, include all stop colors
                    return color.colors.map((stop: any) => stop.color);
                  }

                  return null;
                })
                .flat()
                .filter(Boolean),
            ),
            images: imageUrls,
          });

          // Log extracted information
          console.log('Extracted Fonts:', Array.from(fontsArray));
          console.log('Extracted Colors:', Array.from(colorArray));
          console.log('Extracted Images:', imageUrls);

          // Store in localStorage
          localStorage.setItem('dualite_figma_images', JSON.stringify(imageUrls));

          // Step 2: Fetch the image using the common function with scale 1
          const { imageBase64, imageSizeInMB } = await fetchFigmaImage(figmaUrl);

          // Get image dimensions
          const dimensions = await getImageDimensions(imageBase64);
          setDimensions(dimensions); // Save dimensions to state

          console.log('dimensions', dimensions);

          // Size and dimension validation
          if (imageSizeInMB > MAX_SIZE || dimensions.width > MAX_DIMENSION || dimensions.height > MAX_DIMENSION) {
            setImageDetails({
              quality: imageSizeInMB,
              size: imageSizeInMB,
            });
            setImagePreview(imageBase64);

            // Report error to parent component through onExtractError
            if (imageSizeInMB > MAX_SIZE) {
              toast.error('Design is too large to compress. Please select a smaller design.');
              onExtractError?.({
                image: imageBase64,
                errorType: 'size',
              });
            } else {
              toast.error(
                `Image ${dimensions.width > MAX_DIMENSION ? 'width' : 'height'} exceeds 8000px. Please select a smaller design.`,
              );
              onExtractError?.({
                image: imageBase64,
                errorType: 'dimension',
              });
            }

            return; // Add a return statement here to stop execution
          }

          // Calculate compression details if needed
          const needsCompression = imageSizeInMB > THRESHOLD_SIZE;
          const compressionPercentage = needsCompression ? ((imageSizeInMB - THRESHOLD_SIZE) / imageSizeInMB) * 100 : 0;

          console.log('needsCompression', needsCompression);
          console.log('compressionPercentage', compressionPercentage);

          setImageDetails({
            quality: imageSizeInMB,
            size: imageSizeInMB,
          });

          setImagePreview(imageBase64);

          // Remove localStorage calls and pass data directly through callback
          onExtractComplete({
            image: imageBase64,
            fonts: Array.from(fontsArray),
            colors: Array.from(
              colorArray
                .map((color) => {
                  if (color.type === 'SOLID') {
                    return color.color;
                  } else if (color.colors) {
                    // For gradients, include all stop colors
                    return color.colors.map((stop: any) => stop.color);
                  }

                  return null;
                })
                .flat()
                .filter(Boolean),
            ),
            imageUrls,
          });
          setIsExtractionDone(true);

          if (!needsCompression) {
            toast.success('Design assets extracted successfully');
          }
        },
      );
    } catch (error) {
      Sentry.captureException(error, {
        extra: {
          message: 'Error extracting figma data',
          figmaUrl,
        },
      });

      // Check if the error is due to abortion
      if (axios.isCancel(error)) {
        console.log('Request cancelled');
        return;
      }

      console.error('Extraction error:', error);

      if (error instanceof Error) {
        if (error.message.toLowerCase().includes('No image URL found in the response'.toLowerCase())) {
          setFigmaUrl('');
          toast.error('No image URL found in the response');
          return;
        }

        // Show user-friendly message for node not found error
        if (error.message.includes('Failed to extract design')) {
          toast.error(error.message);
        } else {
          toast.error('Failed to extract design. Please try again.');
        }
      } else {
        toast.error('Failed to extract design. Please try again.');
      }

      handleClearImage();
    } finally {
      setIsExtracting(false);
    }
  };

  const handleClearImage = () => {
    setIsExtractionDone(false);
    onRemoveAttachedImage();
    setImagePreview(null);
    setFigmaUrl('');
    setStyleInfo({
      fonts: new Set(),
      colors: new Set(),
      images: [],
    });

    // Clear all related localStorage items
    localStorage.removeItem('uploadedImage');

    // Clear the attached image in parent component
    onExtractComplete({
      image: null,
      fonts: [],
      colors: [],
      imageUrls: [],
    });
  };

  const FIGMA_BASE_URL = 'https://www.figma.com/';

  const isValidFigmaUrl = (url: string): boolean => {
    console.log('testing validation ', url.startsWith(FIGMA_BASE_URL));

    try {
      if (!url.startsWith(FIGMA_BASE_URL)) {
        return false;
      }

      new URL(url);
      return true;
    } catch {
      return false;
    }
  };
  const handleDisconnectFigma = () => {
    const isConfirmed = window.confirm('Are you sure you want to disconnect your Figma account?');

    if (isConfirmed) {
      // Clear all Figma related data
      localStorage.removeItem('dualite_figma_token');
      localStorage.removeItem('dualite_api_keys');

      // Reset states
      setHasFigmaToken(false);
      setImagePreview(null);
      setKeys(null);

      // Clear the attached image in parent component
      onExtractComplete({
        image: null,
        fonts: [],
        colors: [],
        imageUrls: [],
      });

      // designs should be fully disconnected if logged out.
      handleClearImage();

      toast.success('Figma account has been disconnected successfully');
    }
  };

  // Update handleReduceImageQuality with new compression formula
  const handleReduceImageQuality = async () => {
    if (!imageDetails.quality || imageDetails.quality <= THRESHOLD_SIZE) {
      return;
    }

    // New compression formula
    const compressionPercentage = ((imageDetails.quality - THRESHOLD_SIZE) / imageDetails.quality) * 100;
    const compressedScale = 1 - compressionPercentage / 100;

    console.log('Compression details:', {
      originalSize: imageDetails.quality,
      compressionPercentage,
      compressedScale,
    });

    setIsExtracting(true);

    try {
      const { imageBase64, imageSizeInMB } = await fetchFigmaImage(figmaUrl, { scale: compressedScale });

      setImageDetails({
        quality: imageSizeInMB,
        size: imageSizeInMB,
      });

      setImagePreview(imageBase64);

      onExtractComplete({
        image: imageBase64,
        fonts: Array.from(styleInfo.fonts),
        colors: Array.from(styleInfo.colors),
        imageUrls: styleInfo.images,
      });

      toast.success('Figma design imported successfully');

      if (isExtractionDone) {
        onClose();
      }
    } catch (error) {
      Sentry.captureException(error, {
        extra: {
          message: 'Error compressing image',
        },
      });
      console.error('Image compression error:', error);
      toast.error(error instanceof Error ? error.message : 'Failed to compress image');
    } finally {
      setIsExtracting(false);
    }
  };

  // Add this button text logic
  const handleContinueWithCompression = async () => {
    if (imageDetails.quality > 1) {
      // If image quality exceeds the limit, reduce it first
      setIsExtracting(true);

      try {
        console.log('imageDetails.quality', imageDetails.quality);

        // Calculate new scale using the formula
        const newScale = 3 / (imageDetails.quality - 1);
        console.log('New Scale of image for reduced quality:', newScale.toFixed(2));

        // Fetch image with reduced quality
        const { imageBase64, imageSizeInMB } = await fetchFigmaImage(figmaUrl, { scale: newScale });

        // Update image details
        setImageDetails({
          quality: imageSizeInMB,
          size: imageSizeInMB,
        });

        setImagePreview(imageBase64);

        // Update the extracted data with the new image
        onExtractComplete({
          image: imageBase64,
          fonts: Array.from(styleInfo.fonts),
          colors: Array.from(styleInfo.colors),
          imageUrls: styleInfo.images,
        });

        toast.success('Image quality reduced successfully');

        // Since we're already done with extraction, just close the modal
        if (isExtractionDone) {
          onClose();
        }
      } catch (error) {
        Sentry.captureException(error, {
          extra: {
            message: 'Error reducing image quality',
          },
        });
        console.error('Image quality reduction error:', error);
        toast.error(error instanceof Error ? error.message : 'Failed to reduce image quality');
      } finally {
        setIsExtracting(false);
      }
    } else {
      // If no reduction needed, just close the modal if extraction is done
      if (isExtractionDone) {
        onClose();
      } else {
        // Otherwise proceed with normal extraction
        handleImageUrlSubmit();
      }
    }
  };

  const getButtonText = () => {
    if (isExtracting) {
      return 'Extracting...';
    }

    if (imagePreview) {
      return 'Continue';
    }

    return 'Extract Assets';
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose}>
      <div className="fixed inset-0 bg-black/70 backdrop-blur-sm z-50" />
      <div
        className={classNames(
          'fixed top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 z-51 w-full px-4',
          !imagePreview ? 'max-w-[480px]' : 'max-w-[900px]',
        )}
      >
        <div
          className={classNames(
            'bg-alpha-background-secondary rounded-xl border border-white/10 relative w-full overflow-hidden',
            !imagePreview ? 'max-w-[480px]' : 'max-w-[900px]',
          )}
        >
          <button
            onClick={onClose}
            className="absolute top-4 right-4 p-2 text-[#e7e7e7] bg-transparent border-none rounded-md hover:bg-white/10 transition-colors z-10"
          >
            <X size={16} />
          </button>

          <div className="p-6">
            <h2 className="text-base font-semibold text-[#e7e7e7] m-0 pr-6">
              {hasFigmaToken ? 'Import Your Design' : 'Connect Figma Account'}
            </h2>

            <div className="h-px bg-[#414142] my-3 -mx-6" />

            {hasFigmaToken ? (
              <>
                {!imagePreview ? (
                  <div className="flex flex-col">
                    <div className="mb-4 rounded-lg border border-white/10 bg-[#2c2c2c30] flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 p-3">
                      <div className="flex items-center gap-3">
                        <div className="w-8 h-8 rounded-full bg-[#4f46e5] flex items-center justify-center text-white font-medium overflow-hidden">
                          {userData?.userData?.img_url ? (
                            <img
                              src={userData?.userData?.img_url}
                              alt="Figma User"
                              className="w-full h-full object-cover rounded-full"
                              crossOrigin="anonymous"
                            />
                          ) : (
                            userData?.userData?.email?.charAt(0).toUpperCase()
                          )}
                        </div>
                        <div className="flex flex-col">
                          <span className="text-alpha-text-heading-start text-xs font-normal">Figma Account</span>
                          <span className="text-alpha-text-heading-start font-medium text-sm truncate max-w-[200px]">
                            {userData?.userData?.email || 'No email available'}
                          </span>
                        </div>
                      </div>
                      <button
                        onClick={handleDisconnectFigma}
                        className="flex items-center gap-1.5 px-3 py-1.5 bg-red-500/20 hover:bg-red-500/30 border border-red-500/30 rounded-md text-red-400 text-sm transition-colors whitespace-nowrap"
                      >
                        <LogOut size={14} />
                        Disconnect
                      </button>
                    </div>

                    <div className="flex justify-between items-center mb-3">
                      <label className="text-sm font-medium text-alpha-text-heading-start">
                        Your Figma Design Link
                      </label>
                    </div>

                    <input
                      type="text"
                      value={figmaUrl}
                      onChange={(e) => setFigmaUrl(e.target.value)}
                      placeholder="https://www.figma.com/design/..."
                      className="w-full px-3.5 py-2.5 bg-[#1D1D1D] border border-[#414142] rounded-lg text-[#e7e7e7] text-sm focus:outline-none focus:border-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
                      disabled={isExtracting}
                    />

                    <p className="mt-2 text-xs text-alpha-text-heading-start">
                      Paste the Figma share URL to extract your design & assets
                    </p>

                    <div className="mt-4 mb-4 p-4 bg-[#1D1D1D00] border border-[#414142] rounded-lg">
                      <h3 className="text-sm font-medium text-alpha-text-heading-start mb-2">
                        How to get your Design Link:
                      </h3>
                      <ol className="list-decimal ml-5 space-y-2">
                        <li className="text-xs text-alpha-text-heading-start">Open your design file in Figma</li>
                        <li className="text-xs text-alpha-text-heading-start">
                          Select the specific frame you want to convert
                        </li>
                        <li className="text-xs text-alpha-text-heading-start">
                          Right click → Copy/Paste as → Copy link to selection
                        </li>
                        <li className="text-xs text-alpha-text-heading-start">
                          Paste the copied URL in the field above
                        </li>
                      </ol>
                    </div>

                    <button
                      onClick={handleImageUrlSubmit}
                      disabled={isExtracting || figmaUrl === ''}
                      className="flex items-center justify-center gap-1.5 w-full mt-6 px-2.5 py-2.5 bg-gradient-to-b from-alpha-primary-start to-alpha-primary-end rounded-lg text-white text-sm font-medium hover:brightness-94 transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      {isExtracting ? (
                        <div className="i-svg-spinners:90-ring-with-bg" />
                      ) : (
                        <>
                          <ImageIcon size={16} />
                          <span>Import</span>
                        </>
                      )}
                    </button>

                    <button
                      onClick={handleCancel}
                      className="flex items-center justify-center gap-1.5 w-full mt-3 px-2.5 py-2.5 bg-gray-600 hover:bg-gray-700 rounded-lg text-white text-sm font-medium transition-colors"
                    >
                      {isExtracting ? 'Cancel' : 'Close'}
                    </button>
                  </div>
                ) : (
                  <>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6 mt-6">
                      <div>
                        <div className="flex justify-between items-center mb-3">
                          <label className="text-sm font-medium text-alpha-text-heading-start">Figma Design URL</label>
                        </div>
                        <input
                          type="text"
                          value={figmaUrl}
                          onChange={(e) => setFigmaUrl(e.target.value)}
                          placeholder="https://www.figma.com/design/..."
                          className="w-full px-3.5 py-2.5 bg-[#2c2c2c] border border-white/10 rounded-lg text-[#e7e7e7] text-sm focus:outline-none focus:border-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
                          disabled={isExtracting || isExtractionDone}
                        />
                        <p className="mt-2 text-xs text-[#4b5563]">
                          Paste the Figma share URL to extract design assets
                        </p>
                      </div>

                      <div>
                        <div className="mb-3 rounded-lg border border-white/10 bg-[#2c2c2c30] flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 p-3">
                          <div className="flex items-center gap-3">
                            <div className="w-8 h-8 rounded-full bg-[#4f46e5] flex items-center justify-center text-white font-medium overflow-hidden">
                              {userData?.userData?.img_url ? (
                                <img
                                  src={userData?.userData?.img_url}
                                  alt="Figma User"
                                  className="w-full h-full object-cover rounded-full"
                                  crossOrigin="anonymous"
                                />
                              ) : (
                                userData?.userData?.email?.charAt(0).toUpperCase()
                              )}
                            </div>
                            <div className="flex flex-col">
                              <span className="text-alpha-text-heading-start text-xs font-normal">Figma Account</span>
                              <span className="text-alpha-text-heading-start font-medium text-sm truncate max-w-[120px] md:max-w-[180px]">
                                {userData?.userData?.email || 'No email available'}
                              </span>
                            </div>
                          </div>
                          <button
                            onClick={handleDisconnectFigma}
                            className="flex items-center gap-1.5 px-3 py-1.5 bg-red-500/20 hover:bg-red-500/30 border border-red-500/30 rounded-md text-red-400 text-sm transition-colors whitespace-nowrap"
                          >
                            <LogOut size={14} />
                            Disconnect
                          </button>
                        </div>
                      </div>

                      <div className="md:col-span-2 lg:col-span-1">
                        <h3 className="text-sm font-medium text-alpha-text-heading-start mb-2">Imported Design</h3>
                        <div className="relative flex items-center justify-center rounded-lg overflow-hidden bg-[#2c2c2c] min-h-[250px] max-h-[350px] mb-2.5">
                          <img
                            src={imagePreview}
                            alt="Imported design"
                            className="w-full h-auto max-h-[350px] object-contain"
                          />
                          <button
                            onClick={handleClearImage}
                            className="absolute top-2 right-2 p-1.5 bg-black/50 hover:bg-black/70 rounded-md transition-colors flex text-[#e7e7e7] text-sm font-medium gap-1.5"
                          >
                            Detach Design
                            <X size={16} className="text-alpha-text-heading-start" />
                          </button>
                        </div>
                      </div>

                      <div className="flex flex-col gap-4 max-h-[350px] overflow-y-auto md:col-span-2 lg:col-span-1">
                        {styleInfo.colors.size > 0 && (
                          <div>
                            <h3 className="text-sm font-medium text-alpha-text-heading-start mb-4">
                              Colors ({styleInfo.colors.size})
                            </h3>
                            <div
                              className="flex flex-wrap gap-1.5 max-h-[100px] overflow-y-auto pr-2.5"
                              style={{
                                scrollbarWidth: 'thin',
                                scrollbarColor:
                                  'var(--alpha-background-overlay-primary) var(--alpha-background-tertiary)',
                              }}
                            >
                              {Array.from(styleInfo.colors).map((color) => (
                                <div
                                  key={color}
                                  className="flex items-center gap-1.5 px-3 py-2.5  rounded-md border border-[#414142]"
                                >
                                  <div
                                    className="w-3 h-3 rounded border border-white/10"
                                    style={{ backgroundColor: color }}
                                  />
                                  <span className="text-xs text-white uppercase font-mono">{color}</span>
                                </div>
                              ))}
                            </div>
                          </div>
                        )}

                        {styleInfo.fonts.size > 0 && (
                          <div>
                            <h3 className="text-sm font-medium text-alpha-text-heading-start mb-4">
                              Fonts ({styleInfo.fonts.size})
                            </h3>
                            <div className="flex flex-wrap gap-1.5 max-h-[100px] overflow-y-auto pr-2.5 scrollbar-thin scrollbar-thumb-[var(--alpha-background-overlay-primary)] scrollbar-track-[var(--alpha-background-tertiary)]">
                              {Array.from(styleInfo.fonts).map((font) => (
                                <div key={font} className="px-3 py-1 rounded-md border border-[#414142]">
                                  <span className="text-xs text-white">{font}</span>
                                </div>
                              ))}
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                    {imageDetails.quality > MAX_SIZE ||
                    (dimensions && (dimensions.width > MAX_DIMENSION || dimensions.height > MAX_DIMENSION)) ? (
                      <div className="flex flex-col items-center text-center">
                        <p className="text-sm text-[#ef4444] bg-[#ef4444]/10 px-4 py-2.5 rounded-md max-w-[90%]">
                          {imageDetails.quality > MAX_SIZE
                            ? 'Design is too large to compress. Please select a smaller design.'
                            : dimensions &&
                              `Image ${dimensions.width > MAX_DIMENSION ? 'width' : 'height'} exceeds 8000px. Please select a smaller design.`}
                        </p>
                        <button
                          onClick={handleClearImage}
                          className="flex items-center justify-center gap-1.5 px-5 py-2.5 mt-3 bg-[#ef4444] hover:bg-[#dc2626] rounded-lg text-white text-sm font-medium transition-colors"
                        >
                          Select New Design
                        </button>
                      </div>
                    ) : imageDetails.quality > THRESHOLD_SIZE ? (
                      <div className="flex flex-col items-center text-center">
                        <p className="text-sm text-[#f0a52e] bg-[#f0a52e]/10 px-4 py-2.5 rounded-md max-w-[90%]">
                          Design will be compressed by{' '}
                          {(((imageDetails.quality - THRESHOLD_SIZE) / imageDetails.quality) * 100).toFixed(2)}%
                        </p>
                        <button
                          onClick={handleReduceImageQuality}
                          disabled={isExtracting}
                          className="flex items-center justify-center gap-1.5 w-[60%] px-2.5 py-2.5 mt-3 bg-gradient-to-b from-alpha-primary-start to-alpha-primary-end hover:brightness-94 rounded-lg text-white text-sm font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                          {isExtracting ? (
                            <div className="i-svg-spinners:90-ring-with-bg" />
                          ) : (
                            'Continue with compression'
                          )}
                        </button>
                      </div>
                    ) : (
                      <div className="flex flex-col items-center text-center">
                        <button
                          onClick={() => {
                            onClose();
                            toast.success('Figma design attached successfully');
                          }}
                          disabled={isExtracting}
                          className="flex items-center justify-center gap-1.5 px-10 py-2.5 bg-gradient-to-b from-alpha-primary-start to-alpha-primary-end hover:brightness-94 rounded-lg text-white text-sm font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                          Continue with this Design
                        </button>
                      </div>
                    )}
                  </>
                )}
              </>
            ) : initValidationLoading ? (
              <div className="flex justify-center items-center">
                <div className="i-svg-spinners:90-ring-with-bg" />
              </div>
            ) : (
              <div className="flex flex-col items-center gap-4 text-center">
                <p className="text-[#e7e7e7]/70 text-sm m-0 py-6">Connect your Figma account to access your designs.</p>
                <button
                  onClick={handleGenerateKeys}
                  disabled={isLoading}
                  className="flex items-center justify-center gap-1.5 w-full px-2.5 py-2.5 bg-gradient-to-b from-alpha-primary-start to-alpha-primary-end rounded-lg text-white text-sm font-medium hover:brightness-94 transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {isLoading ? (
                    <div className="i-svg-spinners:90-ring-with-bg" />
                  ) : (
                    <>
                      <Key size={16} />
                      <span>Connect Figma</span>
                    </>
                  )}
                </button>
              </div>
            )}
          </div>
        </div>
      </div>
    </Modal>
  );
}
